"use client"

import { useState } from "react"
import { ChevronRight, Link2, Check, Handshake, Globe } from "lucide-react"
import Navbar from "@/app/Components/NavbarSection"
import Footer from "@/app/Sections/Footer"

function CopyLinkButton({ id }: { id: string }) {
    const [copied, setCopied] = useState(false)
    const copy = () => {
        if (typeof window === "undefined") return
        navigator.clipboard.writeText(`${window.location.origin}${window.location.pathname}#${id}`)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }
    return (
        <button
            onClick={copy}
            aria-label="Copy section link"
            className="flex-shrink-0 p-1.5 rounded-md text-gray-600 hover:text-[#8B5CF6] hover:bg-[#8B5CF6]/10 transition-colors"
        >
            {copied ? <Check className="w-3.5 h-3.5 text-green-400" /> : <Link2 className="w-3.5 h-3.5" />}
        </button>
    )
}

const sections_en = [
    {
        id: "purpose",
        number: "01",
        title: "Purpose of Agreement",
        content: (
            <p className="text-gray-400 font-poppins text-sm leading-relaxed pl-7">
                The purpose of this Agreement is to establish a mutually beneficial relationship where the Partner actively promotes Aurex Hosting’s game server hosting services to their community, and in return, Aurex Hosting provides the Partner with specific hosting resources, affiliate incentives, and technical support.
            </p>
        )
    },
    {
        id: "obligations",
        number: "02",
        title: "Partner Obligations",
        content: (
            <div className="text-gray-400 font-poppins text-sm leading-relaxed pl-7 space-y-3">
                <p>To maintain active partner status, the Partner agrees to fulfill the following promotional requirements:</p>
                <ul className="list-decimal pl-5 space-y-2">
                    <li><strong>Mandatory Promotion:</strong> Publishing content (including videos, live streams, or social media posts) that utilizes our infrastructure without explicitly promoting Aurex Hosting is considered a direct violation of this Agreement.</li>
                    <li><strong>YouTube Specifics:</strong> In all YouTube videos featuring Aurex Hosting servers, the Partner must actively promote Aurex Hosting. Furthermore, the official Aurex Hosting website link and official Discord invite link must be clearly included in the video description.</li>
                    <li><strong>In-Game & Discord Branding:</strong> All sponsored servers must include Aurex Hosting banners in-game, and the Partner must actively promote us in their community Discord server.</li>
                    <li><strong>Minecraft & Server Specifics:</strong> For Minecraft servers (and other applicable game servers), the exact text "Powered By Aurex Hosting" must be prominently displayed in the following locations:
                        <ul className="list-[circle] pl-5 mt-1 space-y-1">
                            <li>The second line of the server MOTD (Message of the Day).</li>
                            <li>The in-game Scoreboard.</li>
                            <li>The in-game Tab list.</li>
                        </ul>
                    </li>
                    <li><strong>Brand Representation:</strong> Maintain a positive, professional public image. The Partner must not engage in behavior that violates platform terms of service or reflects poorly on the Aurex Hosting brand.</li>
                    <li><strong>Exclusivity (Optional):</strong> The Partner agrees not to actively promote competing game server hosting providers for the duration of this Agreement.</li>
                </ul>
            </div>
        )
    },
    {
        id: "benefits",
        number: "03",
        title: "Aurex Hosting Obligations & Benefits",
        content: (
            <div className="text-gray-400 font-poppins text-sm leading-relaxed pl-7 space-y-3">
                <p>In exchange for the Partner’s promotional efforts, Aurex Hosting will provide:</p>
                <ul className="list-decimal pl-5 space-y-2">
                    <li><strong>Sponsored Infrastructure:</strong> A dedicated game server allocation for the Partner’s official community use, free of charge.</li>
                    <li><strong>Event Servers:</strong> Upon request, Aurex Hosting will provision temporary, high-performance servers specifically for the Partner's special events, subathons, or tournaments for a mutually agreed-upon time period.</li>
                    <li><strong>Affiliate Compensation:</strong> A customized promotional code offering a 10-20% discount to new clients, alongside a 5-10% recurring commission for the Partner on all successful referrals.</li>
                    <li><strong>Priority Support:</strong> Expedited technical support and infrastructure assistance for the Partner’s sponsored servers.</li>
                    <li><strong>Co-Marketing:</strong> Occasional amplification of the Partner’s major events, streams, or community milestones through official Aurex Hosting social channels.</li>
                </ul>
            </div>
        )
    },
    {
        id: "brand-guidelines",
        number: "04",
        title: "Brand Guidelines & Intellectual Property",
        content: (
            <div className="text-gray-400 font-poppins text-sm leading-relaxed pl-7 space-y-3">
                <ul className="list-decimal pl-5 space-y-2">
                    <li>The Partner is granted a revocable, non-exclusive license to use Aurex Hosting's official branding materials (logos, banners) solely for the promotional purposes outlined in this Agreement.</li>
                    <li>The Partner must use unmodified brand assets provided by Aurex Hosting to ensure aesthetic consistency and professional presentation.</li>
                    <li>All intellectual property related to Aurex Hosting remains the sole property of Aurex Technologies.</li>
                </ul>
            </div>
        )
    },
    {
        id: "termination",
        number: "05",
        title: "Term & Termination",
        content: (
            <div className="text-gray-400 font-poppins text-sm leading-relaxed pl-7 space-y-3">
                <ul className="list-decimal pl-5 space-y-2">
                    <li><strong>Term:</strong> This Agreement is effective as of the date of signing and will operate on a month-to-month basis.</li>
                    <li><strong>Termination & Data Wiping:</strong> Aurex Hosting reserves the right to immediately terminate this partnership, revoke server access, and completely wipe all server data at any time, without prior notice, if the Partner violates any of our policies, including the failure to properly promote the brand as outlined in Section 2.</li>
                    <li><strong>Voluntary Termination:</strong> Either party may voluntarily terminate this Agreement for any other reason with a written notice of [Number] days.</li>
                </ul>
            </div>
        )
    }
];

const sections_si = [
    {
        id: "purpose",
        number: "01",
        title: "ගිවිසුමේ අරමුණ (Purpose)",
        content: (
            <p className="text-gray-400 font-sinhala [word-spacing:-2px] text-sm font-bold leading-relaxed pl-7">
                මේ Agreement එකේ අරමුණ වන්නේ, Partner විසින් Aurex Hosting හි Game Server Hosting සේවාවන් තමන්ගේ Community එකට සක්‍රීයව Promote කිරීමත්, ඒ වෙනුවට Aurex Hosting විසින් Partner ට අවශ්‍ය Hosting Resources, Affiliate Benefits සහ Tech Support ලබා දීමත් හා දෙපාර්ශවය අතර හොඳ Relationship එකක් ගොඩනගා ගැනීමයි.
            </p>
        )
    },
    {
        id: "obligations",
        number: "02",
        title: "Partner ගේ වගකීම් (Partner Obligations)",
        content: (
            <div className="text-gray-400 font-sinhala [word-spacing:-2px] text-sm font-bold leading-relaxed pl-7 space-y-3">
                <p>Active Partner කෙනෙක් වීමට පහත සඳහන් කරුණු වලට එකඟ විය යුතුය:</p>
                <ul className="list-decimal pl-5 space-y-2">
                    <li><strong>Mandatory Promotion:</strong> Aurex Hosting නිසි අයුරින් Promote කරන්නේ නැතුව අපේ Infrastructure එක පාවිච්චි කර Content (Videos, Live Streams, or Social Media Posts) Publish කරන එක මේ Agreement එක කඩ කිරීමක් (Violation) ලෙස සලකනු ලැබේ.</li>
                    <li><strong>YouTube Specifics:</strong> Aurex Hosting Servers පාවිච්චි කර සිදු කරන හැම YouTube Video එකකම, Partner විසින් Aurex Hosting අනිවාර්යෙන්ම Promote කල යුතුය. ඒ වගේම, Video Description එකේ Official Aurex Hosting Website Link එක සහ Official Discord Invite Link එක පැහැදිලිව අඩංගු කල යුතුය.</li>
                    <li><strong>In-Game සහ Discord Branding:</strong> Sponsored Servers සියල්ලගේම In-Game Aurex Hosting Banners යෙදිය යුතියි. ඒ වගේම Partner ගේ Community Discord Server එක ඇතුලෙත් අපිව සක්‍රීයව Promote කල යුතුයි.</li>
                    <li><strong>Minecraft සහ Server Specifics:</strong> Minecraft Servers (සහ අනිත් අදාළ Servers) වල, "Powered By Aurex Hosting" කියන Text එක මේ තැන්වල අනිවාර්යෙන්ම දර්ශනය විය යුතුයි:
                        <ul className="list-[circle] pl-5 mt-1 space-y-1">
                            <li>Server MOTD එකේ දෙවන පේළියේ (Second Line).</li>
                            <li>In-Game Scoreboard එකේ.</li>
                            <li>In-Game Tab List එකේ.</li>
                        </ul>
                    </li>
                    <li><strong>Brand Representation:</strong> Brand එකේ Reputation එකට හානි වෙන විදිහට වැඩ නොකල යුතු අතර Professional විදිහට තමන්ගේ Community එක Handle කල යුතුයි.</li>
                    <li><strong>Exclusivity (Optional):</strong> මේ Agreement එක තියෙන කාලය ඇතුළත වෙනත් Competing Game Server Hosting Providers ලව Promote නොකරන්න Partner එකඟ විය යුතුයි.</li>
                </ul>
            </div>
        )
    },
    {
        id: "benefits",
        number: "03",
        title: "Aurex Hosting හි වගකීම් සහ ප්‍රතිලාභ (Benefits)",
        content: (
            <div className="text-gray-400 font-sinhala [word-spacing:-2px] text-sm font-bold leading-relaxed pl-7 space-y-3">
                <p>Partner ගේ ප්‍රවර්ධන (Promotion) වැඩකටයුතු වෙනුවෙන්, Aurex Hosting විසින් පහත දේවල් ලබා දෙනු ලැබේ:</p>
                <ul className="list-decimal pl-5 space-y-2">
                    <li><strong>Sponsored Infrastructure:</strong> Partner ගේ Official Community එකේ භාවිතය සඳහා සම්පූර්ණයෙන්ම නොමිලේ (Free of Charge) Dedicated Game Server එකක් ලබා දෙනු ලැබේ.</li>
                    <li><strong>Event Servers:</strong> Partner ගේ අවශ්‍යතාවය අනුව විශේෂ Events, Subathons, හෝ Tournaments වෙනුවෙන් සාකච්චා කර එකඟවු කාල සීමාවකට High-Performance තාවකාලික Servers දෙන්න Aurex Hosting කටයුතු කරනු ලැබේ.</li>
                    <li><strong>Affiliate Compensation:</strong> අලුත් Clients ලට 10-20% ක Discount එකක් දෙන Custom Promo Code එකක් සහ, Successful වෙන සෑම Payment එකකටම Partner ට 5-10% ක Commission එකක් ලබා දෙනු ලැබේ.</li>
                    <li><strong>Priority Support:</strong> Partner ගේ Sponsored Servers වලට ඉක්මන් (Expedited) Technical සහ Infrastructure Support එක ලබා දෙනු ලැබේ.</li>
                    <li><strong>Co-Marketing:</strong> Partner ගේ විශේෂ Events සහ Streams, Official Aurex Hosting Social Channels හරහා අවශ්‍යනම් ප්‍රවර්ධනය කර දීම සිදු කරනු ලැබේ.</li>
                </ul>
            </div>
        )
    },
    {
        id: "brand-guidelines",
        number: "04",
        title: "Brand Guidelines සහ බුද්ධිමය දේපළ (Intellectual Property)",
        content: (
            <div className="text-gray-400 font-sinhala [word-spacing:-2px] text-sm font-bold leading-relaxed pl-7 space-y-3">
                <ul className="list-decimal pl-5 space-y-2">
                    <li>මේ Agreement එකේ තියෙන විදිහට Promote කරන්න පමණක් Aurex Hosting Official Branding Materials (Logos, Banners) පාවිච්චි කරන්න Partner ට අවසර දෙනවා.</li>
                    <li>අපි දෙන Brand Assets කිසිම විදිහකින් වෙනස් කරන්නේ නැතුව (Unmodified) පාවිච්චි කල යුතුය.</li>
                    <li>Aurex Hosting වලට අදාළ සියලුම බුද්ධිමය දේපළ (Intellectual Property) Aurex Technologies සතුව පමණක් පවතින බව සැලකිය යුතුය.</li>
                </ul>
            </div>
        )
    },
    {
        id: "termination",
        number: "05",
        title: "Term & Termination (කාලසීමාව සහ අවලංගු කිරීම)",
        content: (
            <div className="text-gray-400 font-sinhala [word-spacing:-2px] text-sm font-bold leading-relaxed pl-7 space-y-3">
                <ul className="list-decimal pl-5 space-y-2">
                    <li><strong>Term:</strong> මෙම Agreement එක සදහා අදාල පාර්ශවයන් එකඟතාව පල කල දවසේ සිට බලාත්මක වෙන අතර, මාසෙන් මාසෙට (Month-to-Month) යාවත්කාලීන කල යුතුයි.</li>
                    <li><strong>Termination සහ Data Wiping:</strong> Partner විසින් Section 2 එකේ තියෙන විදිහට අපිව හරියට Promote කරන්නේ නැතුව හෝ අපේ Policies කඩ කරොත්, කිසිම පෙර දැනුම්දීමකින් තොරව (Without Prior Notice) ඕනෑම වෙලාවක මේ Partnership එක Terminate කරන්න, Server Access අයින් කරන්න, සහ සියලුම Server Data සම්පූර්ණයෙන්ම Wipe කරලා දාන්න Aurex Hosting එකට සම්පූර්ණ අයිතිය ඇති බව සැලකිය යුතුයි.</li>
                    <li><strong>Voluntary Termination:</strong> Partners ලට දින 3 කට කලින් Aurex Support Ticket එකක් හරහා Notice එකක් ලබා දී ඕනෑම හේතුවක් මත මේ Agreement එකෙන් අයින් වෙන්න පුළුවන්.</li>
                </ul>
            </div>
        )
    }
];

export default function PartnerAgreementPage() {
    const [lang, setLang] = useState<"en" | "si">("en");

    const sections = lang === "en" ? sections_en : sections_si;

    return (
        <div className="bg-black min-h-screen">
            <Navbar />
            <div className="relative overflow-hidden">
                <div className="absolute inset-0 pointer-events-none">
                    <div
                        className="absolute inset-0"
                        style={{
                            backgroundImage: "url('https://cdn.aurex.lk/assets/hero-mc.webp')",
                            backgroundSize: "cover",
                            backgroundPosition: "center top",
                            filter: "blur(6px) brightness(0.45)",
                            transform: "scale(1.05)",
                        }}
                    />
                    <div className="absolute inset-0 bg-[#3b0d6e]/40" />
                    <div
                        className="absolute inset-0"
                        style={{ background: "radial-gradient(ellipse 90% 60% at 50% 110%, #000000 0%, transparent 90%)" }}
                    />
                    <div
                        className="absolute top-0 left-0 right-0 h-40 pointer-events-none"
                        style={{ background: "linear-gradient(to bottom, #000000, transparent)" }}
                    />
                </div>

                <div className="relative z-10">
                    <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-12 pb-24">
                        <nav className="flex items-center gap-1.5 text-xs font-poppins text-gray-500 mb-8">
                            <a href="/" className="hover:text-white transition-colors">Home</a>
                            <ChevronRight className="w-3 h-3" />
                            <span className="text-gray-400">Legal</span>
                            <ChevronRight className="w-3 h-3" />
                            <span className="text-white">Partner Agreement</span>
                        </nav>

                        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-3">
                            <div>
                                <h1 className="font-akira text-3xl sm:text-4xl xl:text-5xl text-white leading-tight tracking-wide mb-3">
                                    Partner Agreement
                                </h1>
                                <p className="text-gray-500 font-poppins text-sm">Last updated: May 25, 2026</p>
                            </div>

                            <div className="flex items-center bg-white/5 border border-white/10 rounded-full p-1 backdrop-blur-md self-start">
                                <button
                                    onClick={() => setLang("en")}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-poppins transition-all ${lang === "en" ? "bg-[#8B5CF6] text-white" : "text-gray-400 hover:text-white"}`}
                                >
                                    <Globe className="w-4 h-4" />
                                    English
                                </button>
                                <button
                                    onClick={() => setLang("si")}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-poppins transition-all ${lang === "si" ? "bg-[#8B5CF6] text-white" : "text-gray-400 hover:text-white"}`}
                                >
                                    <Globe className="w-4 h-4" />
                                    සිංහල
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 pointer-events-none" style={{ lineHeight: 0 }}>
                        <svg width="100%" height="50" viewBox="0 0 2598 150" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMaxYMax meet" style={{ display: "block", position: "relative", zIndex: 20 }}>
                            <path d="M2598 150H0L230.359 0H2598V150Z" fill="black" />
                        </svg>
                    </div>
                </div>
            </div>

            <div className="relative z-10 w-full bg-black">
                <div className="max-w-6xl mx-auto px-4 sm:px-8 py-12 relative">

                    <div className="flex flex-wrap gap-3 mb-10">
                        <a href="/tos" className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/5 bg-white/5 hover:bg-[#8B5CF6]/10 hover:border-[#8B5CF6]/40 transition-all group">
                            <Handshake className="w-4 h-4 text-[#8B5CF6]" />
                            <span className="text-sm font-poppins text-gray-300 group-hover:text-white transition-colors">Terms of Service</span>
                            <ChevronRight className="w-3 h-3 text-gray-600 group-hover:text-[#8B5CF6] transition-colors" />
                        </a>
                        <a href="/privacy" className="flex items-center gap-2 px-4 py-2.5 rounded-full border border-white/5 bg-white/5 hover:bg-[#8B5CF6]/10 hover:border-[#8B5CF6]/40 transition-all group">
                            <Handshake className="w-4 h-4 text-[#8B5CF6]" />
                            <span className="text-sm font-poppins text-gray-300 group-hover:text-white transition-colors">Privacy Policy</span>
                            <ChevronRight className="w-3 h-3 text-gray-600 group-hover:text-[#8B5CF6] transition-colors" />
                        </a>
                    </div>

                    <p className={`text-gray-400 text-sm leading-relaxed mb-10 border-l-4 border-[#8B5CF6] pl-4 ${lang === "si" ? "font-sinhala font-bold [word-spacing:-2px]" : "font-poppins"}`}>
                        {lang === "en" ?
                            "This Partner Agreement (\"Agreement\") is entered into by and between Aurex Technologies (\"Aurex Hosting\") and the undersigned partner (\"Partner\")."
                            :
                            "මෙම Partner Agreement එක (\"ගිවිසුම\") Aurex Technologies (\"Aurex Hosting\") සහ සියලු Active Partners අතර ඇතිකරගන්නා ගිවිසුමකි. සාමාන්‍ය පාරිභෝගියයන්ට අදාල නොවන බව කරුණාවෙන් සලකන්න."
                        }
                    </p>

                    <div className="flex flex-col">
                        {sections.map((s, i) => (
                            <div key={s.id}>
                                <section id={s.id} className="py-8">
                                    <div className="flex items-start gap-3 mb-4">
                                        <span className="font-akira text-[#8B5CF6]/50 text-xs mt-1 flex-shrink-0 select-none">{s.number}</span>
                                        <h2 className={`${lang === "si" ? "font-sinhala font-extrabold text-lg sm:text-xl tracking-tight [word-spacing:-3px]" : "font-akira text-base sm:text-lg"} text-white leading-snug flex-1`}>{s.title}</h2>
                                        <CopyLinkButton id={s.id} />
                                    </div>
                                    {s.content}
                                </section>
                                {i < sections.length - 1 && (
                                    <hr className="border-white/5" />
                                )}
                            </div>
                        ))}
                    </div>

                    <div className="mt-10 pt-8 border-t border-white/5">
                        <p className="text-gray-600 font-poppins text-xs">
                            Questions about our partnership program?{" "}
                            <a href="mailto:partners@aurex.lk" className="text-[#8B5CF6] hover:text-purple-400 transition-colors">
                                partners@aurex.lk
                            </a>
                        </p>
                    </div>
                </div>
            </div>

            <Footer />
        </div>
    )
}
